import { CommonModule } from "@angular/common";
import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
  DxButtonModule,
  DxDataGridComponent,
  DxDataGridModule,
  DxPopoverModule,
  DxSelectBoxModule,
  DxTextBoxModule,
} from "devextreme-angular";
import { exportDataGrid } from "devextreme/excel_exporter";
import { EditorPreparingEvent, RowClickEvent } from "devextreme/ui/data_grid";
import { ItemClickEvent } from "devextreme/ui/select_box";
import { Workbook } from "exceljs";
import { AppGlobals } from "src/app/app.globals";
import {
  DocumentPillComponent,
  DocumentPillValue,
} from "src/app/components/document-pill/document-pill.component";
import {
  Table,
  GroupedStructure,
} from "src/app/interfaces/entities/dynamic-table";
import { DynamicTableService } from "src/app/services/dynamic-tables.service";
import { LoggingService } from "src/app/services/logging.service";
import { ConfigService } from "src/app/services/config.service";
import {
  DTContactValue,
  DTContractValue,
  DTGenericValue,
  DTProjectTaskDocumentValue,
} from "./dt-data-types";
import { FileViewerComponent } from "src/app/components/file-viewer/file-viewer.component";
import {
  ContactPillComponent,
  ContactPillValue,
} from "src/app/components/contact-pill/contact-pill.component";
import { ContractsPillComponent } from "src/app/components/contracts-pill/contracts-pill.component";
import { GpdButtonComponent } from "src/app/components/gpd-button/gpd-button.component";
import { SvgIconComponent } from "src/app/components/svg-icon/svg-icon.component";
import { GenericPopupComponent, PopupButton } from "src/app/components/generic-popup/generic-popup.component";
import { ValidationToastComponent } from "src/app/components/validation-toast/validation-toast.component";
import { ActivityLogService } from "src/app/services/activity-log.service";
import { ActivityEntity, ActivitySection } from "src/app/constants/activity-log";

import { SearchableDropdownDirective } from '../../../directives/searchable-dropdown.directive';
@Component({
    selector: "app-dt-view",
    imports: [
        CommonModule,
        DxSelectBoxModule,
        DxButtonModule,
        DxDataGridModule,
        DxTextBoxModule,
        DxPopoverModule,
        DocumentPillComponent,
        FileViewerComponent,
        ContactPillComponent,
        ContractsPillComponent,
        GpdButtonComponent,
        SvgIconComponent,
        GenericPopupComponent,
        ValidationToastComponent,
        SearchableDropdownDirective,
    ],
    providers: [],
    templateUrl: "./dt-view.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./dt-view.component.css"]
})
export class DynamicTableViewPage implements OnInit {
  tableId: number | null = null;
  tables: Table[] = [];
  selectedTable: any;

  table: any;
  tableStructure: any[] = [];

  groups: any[] = [];

  filterVisible: boolean = false;
  fieldErrors: { fieldId: number; displayName: string; fieldIdentifier: string; message: string }[] = [];

  leavePopupVisible = false;
  tableToLeave: Table | null = null;
  leaveTableButtons: PopupButton[] = [
    {
      text: $localize`:@@yesLeaveTable:Yes, Leave The Table`,
      type: "primary",
      icon: "check",
      onClick: () => this.confirmLeaveTable(),
    },
  ];

  duplicatePopupVisible = false;
  duplicateTitle = "";
  isDuplicating = false;
  duplicateTableButtons: PopupButton[] = [
    {
      text: $localize`:@@cancel:Cancel`,
      type: "secondary",
      onClick: () => {},
    },
    {
      text: $localize`:@@duplicate:Duplicate`,
      type: "primary",
      icon: "copy",
      submitBehavior: true,
      closeOnClick: false,
      onClick: () => this.confirmDuplicateTable(),
    },
  ];

  @ViewChild("grid", { static: false }) grid: DxDataGridComponent;
  @ViewChild("successToast") successToast: ValidationToastComponent;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public appGlobals: AppGlobals,
    private service: DynamicTableService,
    private loggingService: LoggingService,
    private activityLog: ActivityLogService,
    private configService: ConfigService,
  ) {
    this.customizeColumns = this.customizeColumns.bind(this);
  }
  async ngOnInit() {
    this.tableId = parseInt(this.route.snapshot.paramMap.get("id"));
    const beforeId = this.tableId;
    await Promise.all([this.getTables(), this.configService.loadUserList()]);

    if (this.tableId && !beforeId) {
      this.router.navigate([this.tableId], { relativeTo: this.route, replaceUrl: true });
    }

    if (this.selectedTable) {
      await this.getTable();
      this.activityLog.logView({
        Section: ActivitySection.DYNAMIC_TABLE,
        EntityType: ActivityEntity.DYN_TABLE,
        EntityId: this.tableId,
        EntityLabel: this.selectedTable?.title,
      });
    }
  }

  async getTables() {
    this.appGlobals.ShowGlobalLoader();
    try {
      const result = await this.service.getDynamicTableList({});
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
      } else {
        this.tables = result.tables;
        if (this.tableId) {
          this.selectedTable = this.tables.find((x) => x.id == this.tableId);
        }
        if (!this.tableId) {
          this.selectedTable = this.tables.find((x) => x.isDefault);
          this.tableId = this.selectedTable?.id;
        }

        if (!this.tableId && this.tables.length > 0) {
          this.selectedTable = this.tables[0];
          this.tableId = this.selectedTable?.id;
        }
      }
    } catch (e) {
      AppGlobals.ShowErrorMessage(e);
    }
    this.appGlobals.HideGlobalLoader();
  }

  isShared(table: Table): boolean {
    return !!table?.isShared;
  }

  isMine(table: Table): boolean {
    return table?.owner === AppGlobals.LoggedInUserData.email;
  }

  creatorLabel(table: Table): string {
    if (this.isMine(table)) {
      return $localize`:@@createdByMe:Created by Me`;
    }
    const displayName = this.configService.userMap$.value[table.owner] ?? table.owner;
    return `${$localize`:@@createdBy:Created by`} ${displayName}`;
  }

  get leaveTableConfirmMessage(): string {
    const name = this.tableToLeave?.title ?? "";
    return $localize`:@@leaveTableConfirm:<p>Are you sure you want to leave <b>${name}:tableName:</b>?</p><p>You'll lose access to this table and will need to be re-invited to join again.</p>`;
  }

  openLeaveConfirm(table: Table, event: Event) {
    event.stopPropagation();
    this.tableToLeave = table;
    this.leavePopupVisible = true;
  }

  async confirmLeaveTable() {
    if (!this.tableToLeave) return;
    const tableId = this.tableToLeave.id;
    const tableTitle = this.tableToLeave.title;
    this.appGlobals.ShowGlobalLoader();
    try {
      // Minimal payload: the backend only allows a non-owner/non-editor caller to soft-delete
      // their own TableUser row here — it rejects anything else (see WriteDynamicTable's
      // leave-only guard), so structure/filters are intentionally left empty rather than
      // round-tripped through a full get-then-write, which would risk clobbering concurrent
      // edits to the table.
      const writeResult = await this.service.WriteDynamicTable({
        table: {
          id: tableId,
          title: tableTitle,
          structure: [],
          filters: [],
          users: [{ id: 0, email: AppGlobals.LoggedInUserData.email, canEdit: false, isDeleted: true }],
        },
      });
      if (writeResult.hasError) {
        AppGlobals.ShowErrorMessage(writeResult.feedback);
      } else {
        this.tables = this.tables.filter((x) => x.id != tableId);
        if (this.tableId == tableId) {
          const nextTable = this.tables.find((x) => x.isDefault) ?? this.tables[0] ?? null;
          this.selectedTable = nextTable;
          this.tableId = nextTable?.id ?? null;
          if (this.tableId) {
            this.router.navigate([this.tableId], { relativeTo: this.route.parent });
            await this.getTable();
          } else {
            this.table = null;
          }
        }
        this.successToast?.showToast(
          $localize`:@@leftTableSuccess:You have left "${tableTitle}:tableTitle:"`,
          "Success",
        );
      }
    } catch (e) {
      AppGlobals.ShowErrorMessage(e);
    }
    this.tableToLeave = null;
    this.appGlobals.HideGlobalLoader();
  }

  cancelLeaveTable() {
    this.tableToLeave = null;
    this.leavePopupVisible = false;
  }

  async getTable() {
    this.fieldErrors = [];
    this.appGlobals.ShowGlobalLoader();
    try {
      const result = await this.service.getDynamicTableData({
        tableId: this.selectedTable.id,
      });
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
      } else {
        this.table = result.table;
        this.tableStructure = this.table.structure;
        this.filterVisible = true;
        this.fieldErrors = result.fieldErrors ?? [];
      }
    } catch (e) {
      AppGlobals.ShowErrorMessage(e);
    }
    this.appGlobals.HideGlobalLoader();
  }

  mapData(table: Table) {
    return table.structure.map((x) => {
      return {
        id: x.id,
        fieldId: x.fieldId,
        fieldIdentifier: x.fieldIdentifier,
        name: x.displayName,
        sortingIndex: x.sortingIndex,
        group: x.group,
        parentId: x.parentId,
        value1: x.value1,
        value2: x.value2,
        isDeleted: false,
        fields:
          x.fields?.map((y) => {
            return {
              id: y.id,
              fieldId: y.fieldId,
              fieldIdentifier: x.fieldIdentifier,
              name: y.displayName,
              sortingIndex: y.sortingIndex,
              group: y.group,
              parentId: y.parentId,
              value1: y.value1,
              value2: y.value2,
              isDeleted: y.isDeleted,
            };
          }) ?? [],
      };
    });
  }

  tableSelectChanged(e: ItemClickEvent) {
    this.selectedTable = e.itemData;
    this.tableId = this.selectedTable.id;
    this.router.navigate([this.selectedTable.id], {
      relativeTo: this.route.parent,
    });
    this.getTable();
  }

  createTable() {
    this.router.navigate(["0/edit"], { relativeTo: this.route.parent });
  }

  editTable() {
    this.router.navigate([this.selectedTable.id + "/edit"], {
      relativeTo: this.route.parent,
    });
  }

  openDuplicatePopup() {
    const title = this.selectedTable?.title ?? "";
    this.duplicateTitle = $localize`:@@duplicateTableDefaultName:${title}:tableTitle: (Copy)`;
    this.updateDuplicateButtonState();
    this.duplicatePopupVisible = true;
  }

  updateDuplicateButtonState() {
    this.duplicateTableButtons[1].disabled = this.isDuplicating || !this.duplicateTitle?.trim();
  }

  async confirmDuplicateTable() {
    const title = this.duplicateTitle?.trim();
    if (!title || this.isDuplicating || !this.selectedTable) return;

    this.isDuplicating = true;
    this.updateDuplicateButtonState();
    this.appGlobals.ShowGlobalLoader();
    try {
      const result = await this.service.DuplicateDynamicTable({ tableId: this.selectedTable.id, title });
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
      } else {
        this.duplicatePopupVisible = false;
        this.tableId = result.tableId;
        await this.getTables();
        this.router.navigate([this.tableId], { relativeTo: this.route.parent });
        await this.getTable();
        this.successToast?.showToast(
          $localize`:@@duplicateTableSuccess:"${title}:tableTitle:" has been created`,
          "Success",
        );
      }
    } catch (e) {
      AppGlobals.ShowErrorMessage(e);
    }
    this.isDuplicating = false;
    this.updateDuplicateButtonState();
    this.appGlobals.HideGlobalLoader();
  }

  customizeColumns(columns) {
    if (this.tableStructure?.length <= 1) return;
    for (let i = 0; i < this.tableStructure?.length; i++) {
      // columns.push({
      //     // dataField: this.tableStructure[i].fieldIdentifier,
      //     caption: this.tableStructure[i].displayName,
      //     isBand: this.tableStructure[i].group
      // });
      var contactsFields = this.tableStructure[i].fields.map(
        (x) => x.fieldIdentifier,
      );
      for (var j = 0; j < columns.length - 1; j++) {
        if (contactsFields.indexOf(columns[j].dataField) > -1)
          // If the column belongs to "Contacts",
          columns[j].ownerBand = columns.length - 1; // assigns "Contacts" as the owner band column
      }
    }
  }

  calculateCellValue(rowData) {
    let column = this as any;
    const data = rowData[column?.dataField];
    switch (data?.type) {
      case "currency":
        column.dataType = "currency";
        return data.value;
      case "number":
        column.dataType = "number";
        return data.value;
      case "integer":
        column.dataType = "number";
        return data.value;
      case "date":
        column.dataType = "date";
        return data.value ? new Date(data.value) : null;
      default:
        return data?.displayValue;
    }
  }

  calculateDisplayValue(rowData) {
    let column = this as any;
    const data = rowData[column?.dataField];
    switch (data?.type) {
      case "document":
        return this.toDocumentPillValue(data);
      case "contact":
        return this.toContactPillValue(data);
      case "contract":
        return this.toContractPillValue(data);
      default:
        return data?.displayValue;
    }
  }

  exportTable() {
    try {
      const workbook = new Workbook();
      const safeSheetName = (this.table.title ?? 'Export')
        .replace(/[\\\/\?\*\:\[\]]/g, '_')
        .substring(0, 31) || 'Export';
      const worksheet = workbook.addWorksheet(safeSheetName);

      const dateTime = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);

      exportDataGrid({
        component: this.grid.instance,
        worksheet,
        autoFilterEnabled: true,
        customizeCell: ({ gridCell, excelCell }) => {
          if (gridCell.rowType === "data") {
            switch (gridCell.data[gridCell.column.dataField]?.type) {
              case "currency":
                excelCell.numFmt = "#,##0.00 [$€-407]";
                break;
              case "number":
                excelCell.numFmt = "#,##0.00";
                break;
              case "integer":
                excelCell.numFmt = "#,##0";
                break;
              case "date":
                excelCell.numberFormat = "dd.mm.yyyy";
                break;
            }
          }
        },
      }).then(() => workbook.xlsx.writeBuffer())
        .then((buffer) => {
          const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const filename = `${this.table.title}_${dateTime}.xlsx`;
          const url = window.URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.href = url;
          anchor.download = filename;
          anchor.click();
          window.URL.revokeObjectURL(url);
          this.loggingService.logFileExport(blob, filename, {
            section: ActivitySection.DYNAMIC_TABLE,
            entityType: ActivityEntity.DYN_TABLE,
            entityId: this.tableId,
            entityLabel: this.table.title,
          });
        }).catch((err) => {
          AppGlobals.ShowErrorMessage(err);
        });
    } catch (err) {
      AppGlobals.ShowErrorMessage(err);
    }
  }

  toggleDefault() {
    this.table.isDefault = !this.table.isDefault;
    this.selectedTable.isDefault = this.table.isDefault;
    this.service.FavoriteTable({ table: this.table }).then(
      (result) => {
        if (result.hasError) {
          AppGlobals.ShowErrorMessage(result.feedback);
        } else {
          this.tables
            .filter((x) => x.id != this.table.id)
            .forEach((x) => (x.isDefault = false));
        }
      },
      (error) => {
        AppGlobals.ShowErrorMessage(error);
      },
    );
  }

  toDocumentPillValue(value: DTProjectTaskDocumentValue): DocumentPillValue {
    return {
      id: value.value,
      documentName: value.documentName ?? value.displayValue ?? "",
      type: "task",
      externallyModified: value.externallyModified ?? false,
    };
  }

  toContactPillValue(value: DTContactValue): ContactPillValue {
    return {
      id: value.value,
      name: value.displayValue ?? "",
      type: value.contactType,
    };
  }

  toContractPillValue(value: DTContractValue): any {
    return {
      contractId: value.value,
      designation: value.displayValue ?? "",
    };
  }

  onProjectClick(e: RowClickEvent) {
    if (e.data.projectId && e.data.siteId) {
      const url = `site/${e.data.siteId}/project/${e.data.projectId}`;
      window.open(url, '_blank');
    }
  }
}
